import styled from '@xstyled/styled-components';
import { getContrastingColor } from '~/ui/buttons/helpers/getContrastingColor';

/**
 * EDS primary text button (Figma Button).
 * Text-only — no icon. Brand via accentDark / optional `backgroundColor`.
 * Typography: Plus Jakarta Sans 14 / 600 / 20 (not NextButtonEds 16/700).
 *
 * States (same interaction model as Next EDS):
 * - hover: color-mix(accent 70%, white)
 * - active: color-mix(accent 70%, black)
 * - focus: accent outline + white inner ring
 * - disabled: muted grey
 */
export const Button = styled.buttonBox<{
  backgroundColor?: string;
  contrasting?: boolean;
}>`
  position: relative;
  border: none;
  box-sizing: border-box;
  padding: 8px 24px;
  min-height: 46px;
  height: auto;
  border-radius: md;
  background-color: ${p =>
    p.backgroundColor ||
    p.theme?.colors?.accentDark ||
    'var(--color-accent-dk)'};
  color: ${({ backgroundColor }) =>
    backgroundColor ? getContrastingColor(backgroundColor) : '#fff'};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  margin: 0;
  cursor: pointer;
  text-align: center;
  white-space: nowrap;
  font-variant-numeric: lining-nums proportional-nums;
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-size: 14px;
  font-style: normal;
  font-weight: 600;
  line-height: 20px;
  transition:
    background-color 0.15s ease,
    color 0.15s ease;

  &:hover:not(:disabled) {
    ${p =>
      p.contrasting === false
        ? ''
        : p.backgroundColor
          ? `background-color: color-mix(in srgb, ${p.backgroundColor} 70%, white);`
          : `background-color: color-mix(in srgb, var(--color-accent-dk) 70%, white);`}
  }

  &:active:not(:disabled) {
    ${p =>
      p.contrasting === false
        ? ''
        : p.backgroundColor
          ? `background-color: color-mix(in srgb, ${p.backgroundColor} 70%, black);`
          : `background-color: color-mix(in srgb, var(--color-accent-dk) 70%, black);`}
  }

  &:focus {
    outline: 2px solid var(--color-accent-dk);
    outline-offset: 2px;
  }

  &:focus::after {
    content: '';
    display: block;
    position: absolute;
    border-radius: inherit;
    top: -2px;
    left: -2px;
    right: -2px;
    bottom: -2px;
    border: 2px solid white;
    pointer-events: none;
  }

  &:disabled {
    cursor: default;
    background-color: ${p =>
      p.theme?.colors?.greyLight || 'var(--color-grey-lt)'} !important;
    color: ${p =>
      p.theme?.colors?.greyDark || 'var(--color-grey-dk)'} !important;
  }

  &.opened {
    background-color: #792bd5;
  }
`;
